import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { ListPlus, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import {
  getGameContent, addPuzzle, addPuzzlesBulk, updatePuzzle, deletePuzzle,
} from '../../api/games';
import errorText from '../../utils/errorText';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import toast from 'react-hot-toast';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';
import { parsePuzzleList, MAX_BULK_PUZZLES } from './puzzleListImport';

const BLANK_PUZZLE = { prompt: '', answer: '', hints: '', scoreOnCorrect: 1 };

function PuzzleForm({ initial, saving, submitLabel, onSubmit, onCancel }) {
  const { t } = useI18n();
  const uid = useId();
  const [form, setForm] = useState({ ...BLANK_PUZZLE, ...initial });
  const valid = form.prompt.trim() && form.answer.trim();

  return (
    <form
      className="cg-form cg-form--inset"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || saving) return;
        onSubmit({
          prompt: form.prompt.trim(),
          answer: form.answer.trim(),
          hints: form.hints.split('\n').map((h) => h.trim()).filter(Boolean),
          scoreOnCorrect: Number(form.scoreOnCorrect) || 1,
        });
      }}
    >
      <div className="cg-form-field">
        <label className="cg-label" htmlFor={`${uid}-prompt`}>{t('courseAssess.games.questionPromptLabel')}</label>
        <textarea id={`${uid}-prompt`} className="cg-textarea" rows={2} maxLength={2000} data-autofocus placeholder={t('courseAssess.games.questionPromptPlaceholder')} value={form.prompt} onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))} disabled={saving} />
      </div>
      <div className="cg-form-field">
        <label className="cg-label" htmlFor={`${uid}-answer`}>{t('courseAssess.games.correctAnswerLabel')}</label>
        <input id={`${uid}-answer`} className="cg-input" type="text" maxLength={1000} placeholder={t('courseAssess.games.correctAnswerPlaceholder')} value={form.answer} onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))} disabled={saving} />
      </div>
      <div className="cg-form-field">
        <label className="cg-label" htmlFor={`${uid}-hints`}>{t('courseAssess.games.hintsLabel')}</label>
        <textarea id={`${uid}-hints`} className="cg-textarea" rows={2} maxLength={2000} placeholder={t('courseAssess.games.hintsPlaceholder')} value={form.hints} onChange={(e) => setForm((f) => ({ ...f, hints: e.target.value }))} disabled={saving} />
      </div>
      <div className="cg-form-field">
        <label className="cg-label" htmlFor={`${uid}-points`}>{t('courseAssess.games.pointsLabel')}</label>
        <input id={`${uid}-points`} className="cg-input cg-input-score" type="number" min={0} value={form.scoreOnCorrect} onChange={(e) => setForm((f) => ({ ...f, scoreOnCorrect: e.target.value }))} disabled={saving} />
      </div>
      <div className="cg-form-actions">
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onCancel} disabled={saving}>{t('courseAssess.games.cancel')}</button>
        <button type="submit" className={`ds-btn ds-btn-primary ds-btn-sm ${saving ? 'is-loading' : ''}`} disabled={saving || !valid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

/** Many puzzles from a pasted list: preview, line errors, then one all-or-nothing request. */
function PuzzleBulk({ gameId, onAdded, onCancel }) {
  const { t, lang } = useI18n();
  const g = (key) => `gameAuthoring.games.${key}`;
  const [text, setText] = useState('');
  const [step, setStep] = useState('input');
  const [saving, setSaving] = useState(false);
  const { puzzles, errors } = useMemo(() => parsePuzzleList(text), [text]);
  const tooMany = puzzles.length > MAX_BULK_PUZZLES;

  const handleAdd = async () => {
    if (puzzles.length === 0 || tooMany) return;
    setSaving(true);
    try {
      const result = await addPuzzlesBulk(gameId, puzzles.map(({ prompt, answer, hints }) => ({ prompt, answer, hints })));
      const added = result?.puzzles || [];
      toast.success(t(pluralKey(g('bulkAdded'), added.length, lang), { count: added.length }));
      onAdded(added);
    } catch (err) {
      toast.error(errorText(err, t(g('bulkFailed'))));
    } finally {
      setSaving(false);
    }
  };

  const lineErrors = errors.length > 0 && (
    <div className="ui-alert cg-bulk-errors" data-tone="warning" role="status">
      <TriangleAlert aria-hidden="true" />
      <div>
        <p className="cg-bulk-errors-title">
          {t(pluralKey('gameAuthoring.words.errorsTitle', errors.length, lang), { count: errors.length })}
        </p>
        <ul>
          {errors.slice(0, 20).map((err) => (
            <li key={err.line}>
              <strong>{t('gameAuthoring.words.lineLabel', { line: err.line })}</strong>
              {' '}
              <span className="cg-bulk-errors-text">{err.text}</span>
              {' — '}
              {t(g(`puzzleReason.${err.reason}`))}
            </li>
          ))}
          {errors.length > 20 && <li>…</li>}
        </ul>
      </div>
    </div>
  );

  return (
    <div className="cg-bulk">
      <span className="cg-bulk-title">{t(g('bulkTitle'))}</span>
      {step === 'input' ? (
        <>
          <p className="cg-bulk-hint">{t(g('bulkHint'))}</p>
          <textarea
            className="cg-textarea cg-textarea-mono"
            rows={7}
            spellCheck={false}
            data-autofocus
            placeholder={t(g('bulkPlaceholder'))}
            aria-label={t(g('bulkTitle'))}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {lineErrors}
          {text.trim() && puzzles.length === 0 && errors.length === 0 && <p className="cg-empty-hint">{t(g('bulkEmpty'))}</p>}
          <div className="cg-form-actions">
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onCancel}>{t('courseAssess.games.cancel')}</button>
            <button type="button" className="ds-btn ds-btn-primary ds-btn-sm" onClick={() => setStep('preview')} disabled={puzzles.length === 0}>
              {t(g('bulkPreview'))}
            </button>
          </div>
        </>
      ) : (
        <>
          <span className="cg-bulk-count">{t(pluralKey(g('bulkReady'), puzzles.length, lang), { count: puzzles.length })}</span>
          <div className="cg-bulk-table-wrap">
            <table className="cg-bulk-table">
              <thead>
                <tr>
                  <th className="cg-bulk-col-num">#</th>
                  <th>{t(g('colQuestion'))}</th>
                  <th>{t(g('colAnswer'))}</th>
                  <th>{t(g('colHints'))}</th>
                </tr>
              </thead>
              <tbody>
                {puzzles.map((p) => (
                  <tr key={p.line}>
                    <td className="cg-bulk-col-num">{p.line}</td>
                    <td>{p.prompt}</td>
                    <td><strong>{p.answer}</strong></td>
                    <td className={p.hints.length ? '' : 'cg-bulk-blank'}>{p.hints.length ? p.hints.join(' | ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lineErrors}
          {tooMany && (
            <div className="ui-alert" data-tone="danger" role="alert">
              <TriangleAlert aria-hidden="true" />
              <p>{t(g('bulkTooMany'))}</p>
            </div>
          )}
          <div className="cg-form-actions">
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setStep('input')} disabled={saving}>{t(g('bulkBack'))}</button>
            <button type="button" className={`ds-btn ds-btn-primary ds-btn-sm ${saving ? 'is-loading' : ''}`} onClick={handleAdd} disabled={saving || tooMany}>
              {t(pluralKey(g('bulkAction'), puzzles.length, lang), { count: puzzles.length })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A puzzle game's saved puzzles, read back from the API: add one or many, edit and delete.
 * `onStats` hears the puzzle count (the parent only shows a game with puzzles to students).
 */
export default function PuzzleEditor({ game, onStats }) {
  const { t } = useI18n();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [status, setStatus] = useState('loading');
  const [puzzles, setPuzzles] = useState([]);
  // null | 'one' | 'many'
  const [adding, setAdding] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(null);
  const statsRef = useRef(onStats);
  statsRef.current = onStats;

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const content = await getGameContent(game.id);
      setPuzzles(content.puzzles || []);
      setStatus('ready');
    } catch {
      setStatus('failed');
    }
  }, [game.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (status === 'ready') statsRef.current?.({ puzzles: puzzles.length });
  }, [status, puzzles]);

  const handleAdd = async (payload) => {
    setBusy('new');
    try {
      const created = await addPuzzle(game.id, payload);
      setPuzzles((prev) => [...prev, created]);
      setAdding(null);
    } catch (err) {
      toast.error(errorText(err, t('courseAssess.games.addPuzzleFailed')));
    } finally {
      setBusy(null);
    }
  };

  const handleSave = async (pz, payload) => {
    setBusy(pz.id);
    try {
      const updated = await updatePuzzle(game.id, pz.id, payload);
      setPuzzles((prev) => prev.map((p) => (p.id === pz.id ? { ...p, ...updated } : p)));
      setEditingId(null);
      toast.success(t('gameAuthoring.games.saved'));
    } catch (err) {
      toast.error(errorText(err, t('gameAuthoring.games.saveFailed')));
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (pz) => {
    const ok = await confirm({
      title: t('gameAuthoring.games.deletePuzzleTitle'),
      description: t('gameAuthoring.games.deletePuzzleDesc'),
      confirmLabel: t('courseAssess.games.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(pz.id);
    try {
      await deletePuzzle(game.id, pz.id);
      setPuzzles((prev) => prev.filter((p) => p.id !== pz.id));
      toast.success(t('gameAuthoring.games.deleted'));
    } catch (err) {
      toast.error(errorText(err, t('gameAuthoring.games.deleteFailed')));
    } finally {
      setBusy(null);
    }
  };

  if (status === 'loading') {
    return <p className="cg-empty-hint">{t('courseAssess.games.loadingGames')}</p>;
  }
  if (status === 'failed') {
    return (
      <div className="ui-alert" data-tone="danger" role="alert">
        <TriangleAlert aria-hidden="true" />
        <p>{t('gameAuthoring.games.contentLoadFailed')}</p>
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={load}>{t('ui.retry')}</button>
      </div>
    );
  }

  return (
    <div className="cg-editor-inner">
      <div className="cg-editor-toolbar">
        <span className="cg-editor-count">{t('courseAssess.games.puzzlesCount', { count: puzzles.length })}</span>
        {!adding && (
          <span className="cg-editor-toolbar-actions">
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => { setAdding('many'); setEditingId(null); }}>
              <ListPlus aria-hidden="true" />
              {t('gameAuthoring.games.bulkPuzzles')}
            </button>
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => { setAdding('one'); setEditingId(null); }}>
              <Plus aria-hidden="true" />
              {t('courseAssess.games.addPuzzle')}
            </button>
          </span>
        )}
      </div>

      {adding === 'many' && (
        <PuzzleBulk
          gameId={game.id}
          onCancel={() => setAdding(null)}
          onAdded={(added) => { setPuzzles((prev) => [...prev, ...added]); setAdding(null); }}
        />
      )}
      {adding === 'one' && (
        <PuzzleForm
          initial={BLANK_PUZZLE}
          saving={busy === 'new'}
          submitLabel={t('courseAssess.games.addPuzzleBtn')}
          onCancel={() => setAdding(null)}
          onSubmit={handleAdd}
        />
      )}

      {puzzles.length === 0 && !adding && (
        <p className="cg-empty-hint">{t('courseAssess.games.noPuzzlesHint')}</p>
      )}

      <div className="cg-puzzle-list">
        {puzzles.map((pz, idx) => (
          editingId === pz.id ? (
            <PuzzleForm
              key={pz.id}
              initial={{ prompt: pz.prompt, answer: pz.answer, hints: (pz.hints || []).join('\n'), scoreOnCorrect: pz.scoreOnCorrect }}
              saving={busy === pz.id}
              submitLabel={t('gameAuthoring.games.save')}
              onCancel={() => setEditingId(null)}
              onSubmit={(payload) => handleSave(pz, payload)}
            />
          ) : (
            <div key={pz.id} className="cg-puzzle">
              <div className="cg-puzzle-header">
                <span className="cg-puzzle-num">#{idx + 1}</span>
                <span className="cg-node-score">+{pz.scoreOnCorrect} {t('courseAssess.games.pointsSuffix')}</span>
                <span className="cg-node-actions">
                  <button
                    type="button"
                    className="ui-icon-btn"
                    onClick={() => { setEditingId(pz.id); setAdding(null); }}
                    aria-label={`${t('gameAuthoring.games.edit')}: #${idx + 1}`}
                    title={t('gameAuthoring.games.edit')}
                  >
                    <Pencil aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="ui-icon-btn ui-icon-btn--danger"
                    onClick={() => handleDelete(pz)}
                    disabled={busy === pz.id}
                    aria-label={`${t('courseAssess.games.delete')}: #${idx + 1}`}
                    title={t('courseAssess.games.delete')}
                  >
                    <Trash2 aria-hidden="true" />
                  </button>
                </span>
              </div>
              <p className="cg-puzzle-prompt">{pz.prompt}</p>
              <p className="cg-puzzle-answer">{t('courseAssess.games.answerLabel')} <strong>{pz.answer}</strong></p>
              {pz.hints && pz.hints.length > 0 && (
                <p className="cg-puzzle-hints">{t('courseAssess.games.hintsPrefix')} {pz.hints.join(' | ')}</p>
              )}
            </div>
          )
        ))}
      </div>
      {confirmDialog}
    </div>
  );
}
